import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { AlertTriangle, DollarSign, Loader2 } from "lucide-react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

const RefundDialog = ({ 
  open, 
  onOpenChange, 
  registration, 
  onRefundComplete 
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [refundType, setRefundType] = useState("full");
  const [partialAmount, setPartialAmount] = useState("");
  const [reason, setReason] = useState("requested_by_customer");
  const [notes, setNotes] = useState("");
  const [keepInProgram, setKeepInProgram] = useState(false);
  const [manualRefund, setManualRefund] = useState(false);
  const [showManualOption, setShowManualOption] = useState(false);

  if (!registration) return null;

  // Check if this registration has Stripe payment info
  const hasStripePayment = !!(registration.stripe_payment_intent || registration.stripe_session_id);

  const originalAmount = registration.amount || 0;
  const refundAmount = refundType === "full" 
    ? originalAmount 
    : refundType === "75percent"
      ? originalAmount * 0.75
      : refundType === "50percent"
        ? originalAmount * 0.5
        : parseFloat(partialAmount) || 0;

  const handleRefund = async () => {
    if (refundType === "custom" && (!partialAmount || parseFloat(partialAmount) <= 0)) {
      toast.error("Please enter a valid refund amount");
      return;
    }

    if (refundType === "custom" && parseFloat(partialAmount) > originalAmount) {
      toast.error(`Refund amount cannot exceed $${originalAmount.toFixed(2)}`);
      return;
    }

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        registration_id: registration.registration_id,
        reason,
        notes: notes || undefined,
        keep_in_program: keepInProgram,
        manual_refund: manualRefund
      };

      // Set amount based on refund type
      if (refundType !== "full") {
        payload.amount = refundAmount;
      }

      const response = await axios.post(`${API}/payments/refund`, payload, { headers });

      if (response.data.success) {
        const statusMsg = keepInProgram ? " Player remains in the program." : " Player has been removed from the program.";
        const manualMsg = response.data.manual_refund ? " (Manual refund recorded)" : "";
        toast.success(
          response.data.is_full_refund 
            ? `Full refund of $${response.data.amount_refunded.toFixed(2)} processed.${statusMsg}${manualMsg}`
            : `Partial refund of $${response.data.amount_refunded.toFixed(2)} processed.${statusMsg}${manualMsg}`
        );
        
        // Show additional message for manual refunds
        if (response.data.manual_refund && response.data.message) {
          toast.info(response.data.message, { duration: 5000 });
        }
        
        onOpenChange(false);
        if (onRefundComplete) {
          onRefundComplete(response.data);
        }
      }
    } catch (error) {
      console.error("Refund error:", error);
      const errorMessage = error.response?.data?.detail || "Failed to process refund";
      
      // Check if the error is about missing Stripe payment info
      if (errorMessage.includes("No Stripe payment found") || errorMessage.includes("Record manual refund")) {
        setShowManualOption(true);
        toast.error("No Stripe payment found. Enable 'Record manual refund' to continue.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const reasonOptions = [
    { value: "requested_by_customer", label: "Requested by customer" },
    { value: "duplicate", label: "Duplicate registration" },
    { value: "fraudulent", label: "Fraudulent transaction" },
    { value: "program_cancelled", label: "Program cancelled" },
    { value: "other", label: "Other" }
  ];

  const getPlayerName = () => {
    if (registration.player_name) return registration.player_name;
    if (registration.player?.first_name) return `${registration.player.first_name} ${registration.player.last_name}`;
    return "this player";
  };

  const getProgramName = () => {
    if (registration.program_name) return registration.program_name;
    if (registration.program?.name) return registration.program.name;
    return "this program";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-amber-500" />
            Process Refund
          </DialogTitle>
          <DialogDescription>
            Refund for {getPlayerName()}'s registration to {getProgramName()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800">This action cannot be undone</p>
              <p className="text-amber-700 mt-1">
                The refund will be processed through Stripe and credited back to the original payment method.
              </p>
            </div>
          </div>

          {/* Original Amount */}
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Original Payment</span>
              <span className="font-semibold text-slate-900">${originalAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-slate-600">Payment Status</span>
              <span className={`text-sm font-medium ${
                registration.payment_status === 'paid' ? 'text-emerald-600' : 'text-slate-600'
              }`}>
                {registration.payment_status?.charAt(0).toUpperCase() + registration.payment_status?.slice(1)}
              </span>
            </div>
          </div>

          {/* Refund Type */}
          <div className="space-y-2">
            <Label>Refund Type</Label>
            <Select value={refundType} onValueChange={(val) => {
              setRefundType(val);
              // Auto-set partial amount for percentage-based refunds
              if (val === "50percent") {
                setPartialAmount((originalAmount * 0.5).toFixed(2));
              } else if (val === "75percent") {
                setPartialAmount((originalAmount * 0.75).toFixed(2));
              } else if (val === "full") {
                setPartialAmount("");
              }
            }}>
              <SelectTrigger className="rounded-xl" data-testid="refund-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="full">Full Refund (${originalAmount.toFixed(2)})</SelectItem>
                <SelectItem value="75percent">75% Refund (${(originalAmount * 0.75).toFixed(2)})</SelectItem>
                <SelectItem value="50percent">50% Refund (${(originalAmount * 0.50).toFixed(2)})</SelectItem>
                <SelectItem value="custom">Custom Amount</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Amount Input */}
          {refundType === "custom" && (
            <div className="space-y-2">
              <Label htmlFor="partialAmount">Custom Refund Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input
                  id="partialAmount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={originalAmount}
                  placeholder="0.00"
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                  className="pl-7 rounded-xl"
                  data-testid="custom-refund-amount"
                />
              </div>
              <p className="text-xs text-slate-500">Maximum: ${originalAmount.toFixed(2)}</p>
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label>Reason for Refund</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {reasonOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Internal Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this refund..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-xl resize-none"
              rows={3}
            />
          </div>

          {/* Player Status Option */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="keepInProgram"
                checked={keepInProgram}
                onChange={(e) => setKeepInProgram(e.target.checked)}
                className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                data-testid="keep-in-program-checkbox"
              />
              <div>
                <label htmlFor="keepInProgram" className="text-sm font-medium text-blue-900 cursor-pointer">
                  Keep player in the program
                </label>
                <p className="text-xs text-blue-700 mt-1">
                  {keepInProgram 
                    ? "The player will remain enrolled in the program after the refund."
                    : "The player will be removed from the program after the refund."
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Manual Refund Option - shown when no Stripe payment found */}
          {(showManualOption || !hasStripePayment) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="manualRefund"
                  checked={manualRefund}
                  onChange={(e) => setManualRefund(e.target.checked)}
                  className="mt-1 h-4 w-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  data-testid="manual-refund-checkbox"
                />
                <div>
                  <label htmlFor="manualRefund" className="text-sm font-medium text-amber-900 cursor-pointer">
                    Record manual refund
                  </label>
                  <p className="text-xs text-amber-700 mt-1">
                    {manualRefund 
                      ? "This refund will be recorded but NOT processed through Stripe. You must process the actual refund through your original payment method."
                      : "Enable this if the payment was processed outside of Stripe (cash, check, external system)."
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Refund Summary */}
          <div className="bg-slate-900 text-white rounded-xl p-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Refund Amount</span>
              <span className="text-2xl font-bold">${refundAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRefund}
            disabled={loading || (refundType === "custom" && refundAmount <= 0)}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
            data-testid="process-refund-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <DollarSign className="w-4 h-4 mr-2" />
                Process Refund
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RefundDialog;
